// src/ducks/middleware/feature/workbench.middleware.js

/**
 *
 * @description
 *
 * 💢 Hit the extraction endpoint
 *
 * ⏰ async, polling instantiation of the warehouse obsEtl -> warehouse
 *
 * Once we know the job is complete, the workbench page can go
 * ahead and use the graphql server.
 *
 * 💫 run the extraction when hostedWarehouseState === 'STALE'
 *
 * @module middleware/feature/workbench.middleware
 *
 */
import {
  // pass-through document
  TOGGLE_VALUE,
  SET_MSPAN_REQUEST,
  SET_COMP_REDUCED,
  SET_SELECTION_MODEL,
  //
  WORKBENCH, // feature
  ADD_DERIVED_FIELD, // command
  ADD_DERIVED_FIELD_NESTED, // command
  FETCH_WAREHOUSE, // command
  CANCEL_WAREHOUSE, // command
  RESET_CANVAS, // command
  MOVE_TREE, // command from the workbench
  REMOVE_NODE, // command from the workbench
  DND_DRAG_START, // event
  DND_DRAG_END, // event
  setCompReduced, // document
  setCompRequest, // document
  // setQualRequest, // document
  setTree, // document
  setNodeState, // document
  setChildIds, // document
  setDraggedId, // document
  setGroupSemantic, // document
  moveTree,
  resetTree as resetWorkbench,
  tagWarehouseState,
  // MAKE_COMP_SERIES, // command
  // setCompValues, // document
} from '../../actions/workbench.actions';
import {
  SET_MATRIX_CACHE, // command
  fetchMatrixCache,
  tagMatrixState,
} from '../../actions/matrix.actions';
import {
  POLLING_RESOLVED,
  POLLING_ERROR,
  apiCancel,
  apiFetch,
} from '../../actions/api.actions';
import {
  aggregationInputsFromNode,
  exprBuilders,
} from '../../../lib/obsEtlToMatrix/derived-field-expr';
import { bookmark } from '../../actions/stepper.actions';
import { setNotification } from '../../actions/notifications.actions';
import { setUiLoadingState, addError } from '../../actions/ui.actions';
import {
  ApiCallError,
  InvalidStateError,
  ApiResponseError,
  InvalidTreeStateError,
} from '../../../lib/LuciErrors';

import { deleteKeysWithPrefix } from '../../../hooks/use-persisted-state';
import { ServiceConfigs, getServiceType } from '../../../services/api';

// 📖 tree state
import {
  // getProjectId,
  getTree,
  selectNodeState,
  getEtlObject,
  resetCanvas,
  isHostedWarehouseStale,
} from '../../rootSelectors';
import { Tree } from '../../../lib/obsEtlToMatrix/tree';
import {
  COMPUTATION_TYPES,
  PURPOSE_TYPES,
  includeCompInSelectFields,
} from '../../../lib/dataGridSelectionLib';
import { moveItemInArray, removeProp } from '../../../utils/common';
// -----------------------------------------------------------------------------
// normalize obsetl (can include db hosting spec)
import {
  iniEtlUnitMea,
  iniEtlUnitQual,
} from '../../../lib/obsEtlToMatrix/display-obsetl';

// -----------------------------------------------------------------------------

import { NODE_TYPES } from '../../../lib/sum-types';

import { colors } from '../../../constants/variables';

//------------------------------------------------------------------------------
const DEBUG =
  process.env.REACT_APP_DEBUG_MIDDLEWARE === 'true' ||
  process.env.REACT_APP_DEBUG_WORKBENCH_TREE === 'true';
//------------------------------------------------------------------------------
/* eslint-disable no-console */

//------------------------------------------------------------------------------
// Global values
const MAX_TRIES = process.env.REACT_APP_WORKBENCH_MAX_TRIES || 30;
const { isValid, getData, isValidError, getError } =
  ServiceConfigs[getServiceType(WORKBENCH)].response;

/* --------------------------------------------------------------------------- */
const middleware =
  ({ dispatch, getState }) =>
  (next) =>
  async (action) => {
    const state = getState();
    const { projectId } = state.$_projectMeta;
    //
    if (DEBUG) {
      console.info('👉 loaded workbench.middleware');
    }
    if (action.type === 'PING')
      console.log(`%cPING recieved by workbench.middleware`, colors.light.purple);

    // dispatch the current action in action.type with the closure that is
    // about to be returned.
    next(action);

    switch (action.type) {
      //
      case 'VALIDATE_TREE': {
        const tree = getTree(getState());
        const hasDanglingBranches = Object.keys(tree).reduce((acc, key) => {
          const noChildren = tree[key].childIds.length === 0;
          const isLeaf = tree[key].height === 4;
          return acc || (noChildren && !isLeaf);
        }, false);
        next(
          setNotification({
            message: `Dangling branches: ${hasDanglingBranches}`,
            feature: WORKBENCH,
          }),
        );
        break;
      }

      /*
       * Group-specific action
       * Sequence accomplished with repeated calls:
       * 👉 empty -> derived field
       * 👉 derived field -> configured derived field
       *
       * each call maps to a call to update the node's cache
       */
      case ADD_DERIVED_FIELD: {
        // create a state that puts the node into an "ui input-ready" state
        const nodeState = {
          ...action.payload,
          displayType: 'derivedField',
          semantic: 'derivedField',
        };
        next([setGroupSemantic(nodeState)]);
        // send to the top of the middleware stack
        // sagas -> SET_MATRIX_CACHE // document
        dispatch(
          fetchMatrixCache({
            projectId,
            ...nodeState,
          }),
        );

        break;
      }

      // -------------------------------------------------------------------
      // Nest an existing derived field: collapse the current group's
      // function + children into a level-4 derivedFieldInput node,
      // then reset the group so the user can select a new outer function.
      // -------------------------------------------------------------------
      case ADD_DERIVED_FIELD_NESTED: {
        const nodeId = action.payload.id;
        const { tree } = getState().workbench;
        const currentNode = tree[nodeId];

        if (!currentNode?.data?.identifier) {
          console.warn(`[ADD_DERIVED_FIELD_NESTED] node ${nodeId} has no identifier — ignoring`);
          break;
        }

        try {
          const sourceChildIds = currentNode.childIds || [];
          const childrenData = sourceChildIds
            .map((cid) => tree[cid]?.data)
            .filter(Boolean);

          // Create the nested input node data
          const nestedNodeData = {
            type: 'derivedField::input',
            displayType: 'derivedFieldInput',
            label: `${currentNode.data.identifier} (${currentNode.data.groupTag || 'derived'})`,
            functionName: currentNode.data.identifier,
            nestedSourceTree: {
              groupData: { ...currentNode.data },
              children: childrenData,
            },
          };

          // Deep copy the tree to avoid mutation
          const treeState = JSON.parse(JSON.stringify(tree));

          // Generate a new node ID
          const allIds = Object.keys(treeState).map(Number).filter((n) => !Number.isNaN(n));
          const newNodeId = Math.max(...allIds) + 1;

          // Create the nested input node
          treeState[newNodeId] = {
            type: 'canvas',
            height: 4,
            parent: nodeId,
            childIds: [],
            data: nestedNodeData,
            listeners: [],
          };

          // Reset the group: clear function selection, replace children
          treeState[nodeId] = {
            ...treeState[nodeId],
            childIds: [newNodeId],
            data: {
              ...treeState[nodeId].data,
              identifier: undefined,
              cache: undefined,
            },
          };

          // Remove old children from tree
          sourceChildIds.forEach((cid) => {
            delete treeState[cid];
          });

          next(setTree(treeState));
        } catch (err) {
          console.error(`[ADD_DERIVED_FIELD_NESTED] Error:`, err);
        }

        break;
      }

      case SET_MATRIX_CACHE: {
        // forward/pass-through the action proper
        // next(action); 🦀 ??
        // cascade the action to listeners
        const source = action.meta.id;
        const { listeners } = getState().workbench.tree[source];
        if (DEBUG) {
          console.debug(`🔗 listeners`);
          console.dir(listeners);
        }
        next(
          listeners.map((listenerId) =>
            aliasCallbackAction(listenerId, {
              payload: action.payload,
              meta: action.meta,
            }),
          ),
        );
        next(tagMatrixState('STALE'));
        break;
      }
      //
      // 🔖 This next case captures a series of specialized mutations
      //    ... likely a scrappy approach that will benefit from an
      //    improved design.
      //
      // Triggers for clearing parents :: group with a derived field
      // semantic.
      //
      // event -> split/map
      case TOGGLE_VALUE:
      case SET_SELECTION_MODEL:
      case SET_COMP_REDUCED:
      case SET_MSPAN_REQUEST: {
        // check if the parent is a group that needs to be updated
        // const state = getState();
        const { parent } = selectNodeState(state, action.id);
        // reset the parent cache
        const parentState = removeProp('cache', state.workbench.tree[parent].data);
        // if the parent is a derivedField, add/re-add derived field
        // send to the top of the middleware stack
        if (state.workbench.tree[parent].data.displayType === 'derivedField') {
          dispatch({
            type: ADD_DERIVED_FIELD,
            payload: {
              id: parent,
              ...parentState,
            },
          });
        }

        // split the action to update reduced using selectionModel
        if (
          action.type === SET_SELECTION_MODEL &&
          action.purpose === PURPOSE_TYPES.MCOMP
        ) {
          next([
            setCompReduced({
              ...action,
              payload: action.payload.computationType === COMPUTATION_TYPES.REDUCE,
            }),
            setCompRequest({
              ...action,
              payload: includeCompInSelectFields(action.payload),
            }),
          ]);
        }

        next(tagMatrixState('STALE'));
        break;
      }

      // event -> map to other event (use dispatch)
      case DND_DRAG_START: {
        next(setDraggedId(+action.payload.draggableId));
        break;
      }

      case DND_DRAG_END: {
        next(setDraggedId(null));
        // transform/pre-process the action to interpret a "combine"
        // dropped with drop-on elements event
        const {
          draggableId,
          destination = undefined,
          source = undefined,
        } = eventFromCombine(action.payload);

        /* eslint-disable no-param-reassign */
        action.draggableId = draggableId;
        action.payload.source = source;
        action.payload.destination = destination;
        /* eslint-enable no-param-reassign */

        if (typeof destination === 'undefined' || typeof source === 'undefined') {
          break;
        }

        if (
          destination.droppableId === source.droppableId &&
          destination.index === source.index
        ) {
          break;
        }

        const treeState = getTree(getState());

        // same list, different index
        if (destination.droppableId === source.droppableId) {
          next(
            setChildIds({
              id: destination.droppableId,
              childIds: moveItemInArray(
                treeState[destination.droppableId].childIds,
                source.index,
                destination.index,
              ),
            }),
          );
          next(tagMatrixState('STALE'));
          break;
        }

        // else, update the tree
        dispatch(moveTree(action.payload));
        next(tagMatrixState('STALE'));

        break;
      }

      // 🚧 It may reduce re-renders if we compare the tree state before and after;
      // limit the changes/updates accordingly.
      case REMOVE_NODE: {
        // retrieve ids to clear any locally persisted idb-keyval
        const { id, childIds = [] } = selectNodeState(state, action.payload);
        // instantiate the Tree instance to compute changes
        const treeState = getTree(state); // flat tree
        const tree = Tree.fromFlatNodes(treeState);
        try {
          const updatedTree = Tree.removeNode(tree, {
            type: 'remove',
            nodeId: action.payload,
            DEBUG,
          });
          // clear any locally persisted idb-keyval
          // TODO: create a central lookup for how each component
          // uses use-persisted-state
          // v0.4.0
          const keysToDelete = [id, ...childIds];

          keysToDelete.forEach(async (i) => {
            const key = `EtlUnitBase-${i}`;
            try {
              await deleteKeysWithPrefix(key, projectId);
            } catch (error) {
              console.error('Error deleting keys with prefix:', key, error);
            }
          });

          // Clean stale references: remove deleted node IDs from
          // other nodes' listeners and cache arrays.
          // Without this, SET_MATRIX_CACHE cascade dispatches
          // SET_NODE_STATE for nodes that no longer exist.
          const flatUpdated = Tree.toFlatNodes(updatedTree.root);
          const removedIds = new Set(keysToDelete.map(String));

          Object.keys(flatUpdated).forEach((nodeKey) => {
            const node = flatUpdated[nodeKey];

            // Clean listeners
            if (Array.isArray(node.listeners)) {
              node.listeners = node.listeners.filter(
                (lid) => !removedIds.has(String(lid)),
              );
            }

            // Clean cache references (cache is typically [[fieldName]])
            // but can contain node IDs in some contexts
            if (Array.isArray(node.data?.cache)) {
              node.data.cache = node.data.cache
                .map((entry) => {
                  if (Array.isArray(entry)) {
                    return entry.filter((v) => !removedIds.has(String(v)));
                  }
                  return entry;
                })
                .filter((entry) => {
                  if (Array.isArray(entry)) return entry.length > 0;
                  return !removedIds.has(String(entry));
                });
            }
          });

          next([
            setTree(flatUpdated),
            setNotification({
              message: `removed node: ${action.payload}`,
              feature: WORKBENCH,
            }),
          ]);
        } catch (e) {
          if (e instanceof InvalidTreeStateError) {
            next(setNotification({ message: e.message, feature: WORKBENCH }));
          } else {
            throw e;
          }
        }
        next(tagMatrixState('STALE'));
        break;
      }

      // 🚧 It may reduce re-renders if we compare the tree state before and after;
      // limit the changes/updates accordingly.
      case MOVE_TREE: {
        /**
         * Interpretation of the move:
         * 👉 source palette is a COPY semantic
         * 👉 source canvas is a COPY semantic
         * 👉 source palette, set the source id to parent
         */
        const {
          destination = undefined,
          source = undefined,
          draggableId,
        } = action.payload;
        const treeState = getTree(getState()); // flat tree

        //------------------------------------------------------------------------
        // MOVE or COPY based on source === palette
        let moveOrCopy = 'COPY';
        const dataMaker = {
          maker: (x) => {
            return x;
          },
          source: +draggableId,
        };

        const srcPalette = treeState[source.droppableId].type === NODE_TYPES.PALETTE;
        const dstPalette =
          treeState[destination.droppableId].type === NODE_TYPES.PALETTE;

        let message = '';
        switch (true) {
          case srcPalette && dstPalette:
            message = '⬜ nothing';
            return; // nothing to do
          case srcPalette && !dstPalette:
            message = '✨ copy';
            moveOrCopy = 'COPY';
            break;
          case !srcPalette && !dstPalette:
            message = '🟢 move';
            moveOrCopy = 'MOVE';
            break;
          case !srcPalette && dstPalette:
            message = '🚫 delete';
            moveOrCopy = 'MOVE'; // removed without changing palette
            break;
          default:
            message = '📬 What??';
            break;
        }

        //------------------------------------------------------------------------
        // Derived field → derived field: nest as a level-4 input node.
        // The source group's computation spec and UI state are captured in
        // the new node's data so it can be expanded back on drag-out.
        if (
          treeState[draggableId].data.displayType === 'derivedField' &&
          treeState[destination.droppableId].data.displayType === 'derivedField'
        ) {
          moveOrCopy = 'COPY';
          if (DEBUG) console.debug(`🚧 Nesting derived field as level-4 input`);

          const sourceGroup = treeState[draggableId];
          const sourceChildIds = sourceGroup.childIds || [];

          // Snapshot the source group's children data for reconstruction
          // and for series expansion at request time.
          const childrenData = sourceChildIds
            .map((cid) => treeState[cid]?.data)
            .filter(Boolean);

          dataMaker.maker = () => {
            return {
              type: 'derivedField::input',
              displayType: 'derivedFieldInput',
              label: `${sourceGroup.data.identifier} (${sourceGroup.data.groupTag || 'derived'})`,
              functionName: sourceGroup.data.identifier,
              // The source group's function + children — used by
              // buildDerivedExprsFromNode to expand series at request time.
              // NOT a pre-computed spec; the expansion happens lazily.
              nestedSourceTree: {
                groupData: { ...sourceGroup.data },
                children: childrenData,
              },
            };
          };
        }
        if (DEBUG) {
          console.debug(message);
        }

        // interpret the event when coming from the palette to move from leaf to group
        const event = {
          ...action.payload,
          draggableId: srcPalette ? treeState[draggableId].parent : draggableId,
          moveOrCopy,
          type: 'DND',
        };

        // instantiate the Tree instance to compute changes
        const tree = Tree.fromFlatNodes(treeState);

        let before = '';
        if (DEBUG) {
          before = Tree.print(tree);
          console.dir(event);

          console.assert(tree.type === 'root');
          console.assert(tree.children[0].type === 'palette');
          console.debug(`👉 update with: ${moveOrCopy}`);
        }

        try {
          const updatedTree = Tree.moveNode(tree, {
            event, // may have been updated
            dataMaker,
            DEBUG,
          });

          if (DEBUG) {
            console.debug('📖 before');
            console.debug(before);

            console.debug('👉 event');
            console.dir(action.payload);

            console.debug('👉 dragged node');
            console.dir(selectNodeState(state, +action.payload.draggableId));

            console.debug('👉 destination node');
            console.dir(
              selectNodeState(state, +action.payload.destination.droppableId),
            );

            console.debug(`🚧 updated node `);
            console.debug(Tree.print(updatedTree));

            console.debug(`🚧 after tree `);
            console.debug(Tree.print(updatedTree.root));
          }

          next([
            setTree(Tree.toFlatNodes(updatedTree.root)),
            setNotification({
              message: `updated flat tree`,
              feature: WORKBENCH,
            }),
          ]);
        } catch (e) {
          if (e instanceof InvalidTreeStateError) {
            next(setNotification({ message: e.message, feature: WORKBENCH }));
          } else {
            throw e;
          }
        }

        next(tagMatrixState('STALE'));
        break;
      }

      case RESET_CANVAS: {
        next(setTree(resetCanvas(getState())));
        next(tagMatrixState('STALE'));
        break;
      }
      // -------------------------------------------------------------------------
      // Fire-up the api core service
      // 📖 when hostedWarehouseState === 'STALE'
      //    render the warehouse (extraction)
      // -------------------------------------------------------------------------
      // map feature command -> api command
      // ui perspective -> api perspective
      // fetchWarehouse -> apiFetch
      // -------------------------------------------------------------------------
      case FETCH_WAREHOUSE: {
        // the payload required to make the warehouse request is pulled directly
        // from the redux store (nothing in the action).
        //
        // rebuild warehouse (changed etlObj) | use the current tree
        // 🔖 there is no reason (yet), to pull from graphql more than once, once the
        //    tree has been instantiated
        if (!isHostedWarehouseStale(getState())) {
          next(
            setNotification({
              message: `${WORKBENCH}.middleware: Warehouse cache is valid; no need re-render the warehouse`,
              feature: WORKBENCH,
            }),
          );
        } else {
          try {
            next([
              setNotification({
                message: `${WORKBENCH}.middleware: action::feature -> ::api (next: polling-api.sagas)`,
                feature: WORKBENCH,
              }),
              setUiLoadingState({
                toggle: true,
                feature: WORKBENCH,
                message: `Creating (or recreating) the ETL warehouse`,
              }),
              resetWorkbench(),
              /*-----------------------------------------------------------------*/
              apiFetch({
                /*-----------------------------------------------------------------*/
                // ::event
                meta: { uiKey: action.payload, feature: WORKBENCH },
                request: {
                  project_id: projectId,
                  // etlObject: prepareForTransit(getEtlObject(state)), // config for extraction
                  etlObject: getEtlObject(state), // config for extraction
                  maxTries: MAX_TRIES,
                },
              }), // map + translation
            ]);
          } catch (e) {
            // ERROR will have been thrown/catched if deeper
            console.error(e);
            next(
              setNotification({
                message: `${WORKBENCH}.middleware: ${e?.message || e}`,
                feature: WORKBENCH,
              }),
            );
          }
        }
        break;
      }

      // take ui perspective -> api perspective
      case CANCEL_WAREHOUSE: {
        try {
          // feature -> core
          next([
            setNotification({
              message: `${WORKBENCH} middleware: action::feature -> ::api (next: polling-api.sagas)`,
              feature: WORKBENCH,
            }),
            apiCancel({
              // ::eventInterface for the polling-machine
              meta: { uiKey: action.payload, feature: WORKBENCH },
            }), // map + translation
          ]);
        } catch (e) {
          next(
            setNotification({
              message: `${WORKBENCH} middleware: ${e?.message ?? e}`,
              feature: WORKBENCH,
            }),
          );
        } finally {
          next(
            setUiLoadingState({
              toggle: false,
              feature: WORKBENCH,
              message: 'Done cancel warehouse',
            }),
          );
          next(tagWarehouseState('STALE'));
        }
        break;
      }

      // -------------------------------------------------------------------------
      // Respond to events from the api (configured by asyncEtl below)
      // -------------------------------------------------------------------------
      // The response has a view of the obsEtl (representation of the warehouse)
      // api event for this feature -> document feature
      case `${WORKBENCH} ${POLLING_RESOLVED}`: {
        if (DEBUG) {
          console.log(`👉 middleware - resolved`);
          console.log(action);
        }
        // expects event
        //
        // 🔑 Dependency on request + machine output structure
        //
        if (!isValid(action?.event?.request)) {
          console.dir(action);
          throw new ApiResponseError(
            'workbench.middleware: unexpected response; see api.ServiceConfigs',
          );
        }
        try {
          // pull project_id, graphql data, and etlObj
          const { id, subject, measurements } = getData(action.event.request);
          const { etlFields, etlUnits } = getEtlObject(getState());

          if (id !== projectId) {
            throw new InvalidStateError(`Mismatch project: graphql and middleware`);
          }

          next(
            setUiLoadingState({
              toggle: true,
              feature: WORKBENCH,
              message: `Initializing the workbench`,
            }),
          );
          //
          // Normalizer-like: raw -> tree
          //
          // ⬜ This should be something we can configure to better encapsulate
          //    the tree structure vs how it can be used.
          //
          const tree = Tree.fromObsEtl(
            {
              qualities: subject.qualities.map(iniEtlUnitQual(subject.subjectType)),
              measurements: measurements.map(iniEtlUnitMea({ etlFields, etlUnits })),
            },
            3 /* ⚠️  number of canvas stubs (superGroups) */,
          );

          // document the flat version of the tree
          next([
            setTree(Tree.toFlatNodes(tree)),
            tagWarehouseState('CURRENT'),
            tagMatrixState('STALE'),
          ]);
        } catch (e) {
          if (e instanceof ApiCallError) {
            next(
              setNotification({
                message: `${WORKBENCH}.middleware: Error graphql not ready`,
                feature: WORKBENCH,
              }),
            );
          } else {
            next(
              setNotification({
                message: e.message,
                feature: WORKBENCH,
              }),
            );
          }
        } finally {
          next(
            setUiLoadingState({
              toggle: false,
              feature: WORKBENCH,
              message: 'Done processing new warehouse',
            }),
          );
        }

        break;
      }

      // action :: pollingEventError
      case `${WORKBENCH} ${POLLING_ERROR}`: {
        if (!isValidError(action?.event?.request)) {
          console.dir(action);
          throw new ApiResponseError(
            'workbench.middleware: unexpected response; see api.ServiceConfigs',
          );
        }
        // see polling-worker failed to see how sends error
        console.assert(action.event.request?.error, 'The response is not an error');

        next([
          setNotification({
            message: action.event.request?.data?.message ?? 'Api returned an error',
            feature: WORKBENCH,
            error: getError(action.event.request) ?? JSON.stringify(action.event),
          }),
          setUiLoadingState({
            toggle: false,
            feature: WORKBENCH,
            message: 'Done workbench with error',
          }),
          addError({
            feature: WORKBENCH,
            message: action.event.request?.data?.message ?? 'Api returned an error',
          }),
          // display the etlUnit fields page
          bookmark('fields'),
        ]);
        break;
      }
      default:
    }
  };

/**
 * 🚧 expand the interpretation to combine derived fields
 *
 * task create a synthetic event where
 * 👉 draggableId = draggableId
 * 👉 destinationId = combine.draggableId (crux of the interpretation)
 *
 * @function
 * @param {Object} event dnd event
 * @return {Object} dnd event
 */
function eventFromCombine(event) {
  if (!event.combine) {
    return event;
  }
  return {
    ...event,
    destination: { droppableId: event.combine.draggableId, index: null },
  };
}

/**
 * An alias needs to coordinate state with the source.
 *
 * This function is the callback for registered listeners.
 *
 *    action updatedSourceData -> undatedAliasData
 *
 *
 * @function
 * @param {Object} sourceData
 * @return {Object} action
 */
function aliasCallbackAction(nodeId, { payload, meta }) {
  // remove props we don't want to over-write
  const immutableProps = [
    // don't change these
    'source',
    'displayType',
    'type',
    'etlUnitType',
    'tag',
    // ignore these
    'normalizer',
    'feature',
  ];
  const action = setNodeState({
    ...removeProp(immutableProps, meta),
    cache: payload,
    id: nodeId,
  });
  return action;
}

/**
 * Build a DerivedFieldExpr from a derived field group in the flat tree.
 * Used at drop time to capture the computation spec for nesting.
 *
 * @param {Object} flatTree - the flat tree state (workbench.tree)
 * @param {number|string} groupId - the derived field group node id
 * @returns {Object|null} DerivedFieldExpr or null if not buildable
 */
function _buildSpecFromFlatTree(flatTree, groupId) {
  const groupNode = flatTree[groupId];
  if (!groupNode) return null;

  const fnId = groupNode.data?.identifier;
  const builder = exprBuilders[fnId];
  if (!builder) return null;

  const childIds = groupNode.childIds || [];
  const children = childIds
    .map((cid) => flatTree[cid])
    .filter((c) => c?.data?.value?.measurementType);

  if (children.length === 0) return null;

  // For single-input functions (fmap), use the first child
  // For multi-input functions, use all children
  const firstChild = children[0];
  const expanded = aggregationInputsFromNode(firstChild.data.value);

  // Return the first expanded expression (simplification for now)
  // TODO: handle multi-input derived fields and series expansion
  if (expanded.length > 0) {
    const { input } = expanded[0];
    return builder(`${firstChild.data.value.measurementType}.derivedField::${fnId}`, input);
  }

  return null;
}

export default middleware;
